import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, Download, Home, Mail, Phone, AlertCircle } from "lucide-react";

import { getPublicCmaReport, downloadPublicCmaPdf } from "../api/prospectingReportsApi";
import collingsLogo from "../../../assets/collingslogo.png";
import PriceStatementPublic from "../components/PriceStatementPublic";

const NAVY = "#1f2a44";

const money = (n) => (n == null || n === "" ? "" : `$${Number(n).toLocaleString("en-AU")}`);

const range = (low, high) => {
  const lo = money(low);
  const hi = money(high);
  if (lo && hi) return lo === hi ? lo : `${lo} to ${hi}`;
  return lo || hi || "";
};

const auDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
};

const Tile = ({ label, value }) => (
  <div className="p-4 rounded-xl bg-white border border-gray-100">
    <p className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">{label}</p>
    <p className="text-xl font-bold text-gray-900 mt-1">{value}</p>
  </div>
);

const ComparableRow = ({ c, rental }) => (
  <div className="flex items-center gap-3 p-3 bg-white rounded-xl border border-gray-100">
    <div className="w-20 h-16 rounded-lg bg-gray-100 flex-shrink-0 overflow-hidden flex items-center justify-center">
      {c.image_url
        ? <img src={c.image_url} alt="" className="w-full h-full object-cover" />
        : <Home className="w-5 h-5 text-gray-300" />}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-semibold text-gray-900 truncate">{c.full_address}</p>
      <p className="text-xs text-gray-500">
        {[c.suburb, c.state].filter(Boolean).join(" ")}
        {c.bedrooms != null && ` · ${c.bedrooms} bed`}
        {c.bathrooms != null && ` · ${c.bathrooms} bath`}
        {c.land_size ? ` · ${Math.round(c.land_size)}m²` : ""}
        {c.distance_km != null ? ` · ${c.distance_km}km away` : ""}
      </p>
    </div>
    <div className="text-right flex-shrink-0">
      <p className="text-sm font-bold text-gray-900">
        {money(c.price) ? `${money(c.price)}${rental ? " pw" : ""}` : c.price_label || "-"}
      </p>
      <p className="text-xs text-gray-500">{auDate(c.sold_date || c.listed_date)}</p>
    </div>
  </div>
);

/**
 * The digital property report a vendor opens from the emailed link.
 *
 * A PUBLIC route: no login, no sidebar, no CRM chrome. The share token in the
 * URL is the only credential, and the backend hides every internal field. It
 * lives in the frontend because crm.collings.com.au serves only the SPA, so a
 * Django-hosted link on that host renders a blank page.
 *
 * Sections, cover template and theme colour all come from Report Setup. A
 * Property Price Statement renders as the statutory form instead.
 */
const CmaPublicReportPage = () => {
  const { token } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    getPublicCmaReport(token)
      .then(({ data }) => setReport(data))
      .catch((err) => {
        setError(
          err?.response?.status === 410
            ? "This report link has expired. Please contact your agent for an up-to-date report."
            : "We couldn't find that report. Check the link, or ask your agent to resend it.",
        );
      })
      .finally(() => setLoading(false));
  }, [token]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const { data } = await downloadPublicCmaPdf(token);
      const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${report?.report_type_label || "Property report"}, ${report?.full_address || "report"}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError("");
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-400">
        <Loader2 className="w-7 h-7 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-gray-50">
        <div className="max-w-md text-center">
          <AlertCircle className="w-10 h-10 mx-auto text-gray-300 mb-3" />
          <p className="text-sm text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (report.report_type === "price_statement") {
    return <PriceStatementPublic report={report} onDownload={handleDownload} downloading={downloading} />;
  }

  const theme = report.theme_colour || NAVY;
  const template = report.template || "modern";
  const rental = report.report_type === "rental";
  const sections = report.sections || {};
  const show = (key) => sections[key] !== false;
  const headline = report.insights?.headline || {};
  const enabled = new Set(report.insights?.enabled || Object.keys(headline));
  const byGroup = (group) => (report.comparables || []).filter((c) => (c.group || "suburb") === group);

  const saleRange = range(report.sale_estimate_min, report.sale_estimate_max);
  const rentRange = range(report.rental_estimate_min, report.rental_estimate_max);

  const tiles = [
    ["median_sale_price", "Median sale price", money(headline.median_sale_price)],
    ["sold_volume", "Sold volume", headline.sold_volume],
    ["price_growth_pct", "Price growth",
      headline.price_growth_pct != null ? `${headline.price_growth_pct}%` : null],
    ["median_days_on_market", "Median days on market", headline.median_days_on_market],
    ["rental_yield_pct", "Rental yield",
      headline.rental_yield_pct != null ? `${Number(headline.rental_yield_pct).toFixed(1)}%` : null],
    ["clearance_rate_pct", "Clearance rate",
      headline.clearance_rate_pct != null ? `${Math.round(headline.clearance_rate_pct)}%` : null],
  ].filter(([key, , value]) => enabled.has(key) && value != null && value !== "");

  const heading = (text) => (
    <h2
      className={`text-lg font-bold text-gray-900 mb-3 pb-2 ${template === "minimal" ? "border-b" : "border-b-2"} ${template === "classic" ? "font-serif" : ""}`}
      style={{ borderColor: template === "minimal" ? "#e5e7eb" : theme }}
    >
      {text}
    </h2>
  );

  const groups = [
    ["history", "Property history"],
    ["street", rental ? "Comparable rentals (street)" : "Comparable sales (street)"],
    ["suburb", rental ? "Comparable rentals (suburb)" : "Comparable sales (suburb)"],
    ["listing", "Current listings"],
  ];

  const criteria = report.search_criteria || {};
  const criteriaRows = [
    ["Suburb", [report.suburb, report.state, report.postcode].filter(Boolean).join(" ")],
    ["Date from", criteria.date_from],
    ["Date to", criteria.date_to],
    ["Property type", criteria.property_type],
    ["Price from", money(criteria.price_min)],
    ["Price to", money(criteria.price_max)],
    ["Bedrooms from", criteria.beds_min],
    ["Bedrooms to", criteria.beds_max],
    ["Include surrounding", criteria.include_surrounding && criteria.radius_km ? `${criteria.radius_km}km` : ""],
  ].filter(([, v]) => v !== undefined && v !== null && v !== "");

  const modern = template === "modern";

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-5 py-4 flex items-center justify-between">
          <img src={collingsLogo} alt="Collings Real Estate" className="h-7 w-auto object-contain" />
          <button
            type="button"
            onClick={handleDownload}
            disabled={downloading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Download PDF
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 py-8">
        {/* Cover, styled by the template picked on Report Setup. */}
        <section
          className={`mb-8 ${modern ? "rounded-xl p-5" : ""} ${template === "classic" ? "text-center" : ""}`}
          style={modern ? { backgroundColor: theme } : undefined}
        >
          <p className="text-[11px] font-bold uppercase tracking-[0.14em] mb-1" style={{ color: modern ? "#fff" : theme }}>
            {report.report_type_label || "CMA Report"}
          </p>
          <h1 className={`text-3xl font-bold ${modern ? "text-white" : "text-gray-900"} ${template === "classic" ? "font-serif" : ""}`}>
            {report.full_address}
          </h1>
          <p className={`text-sm mt-1 ${modern ? "text-white/80" : "text-gray-500"}`}>
            Prepared{report.recipient_name ? ` for ${report.recipient_name}` : ""}
            {report.prepared_by ? ` by ${report.prepared_by}` : ""} on {auDate(report.created_at)}
          </p>

          {report.image_url ? (
            <img src={report.image_url} alt="" className="w-full h-64 object-cover rounded-xl mt-4" />
          ) : (
            <div className="w-full h-40 rounded-xl mt-4 bg-white border border-gray-100 flex items-center justify-center">
              <Home className="w-8 h-8 text-gray-300" />
            </div>
          )}
        </section>

        {show("agent_profile") && report.agent && (
          <section className="mb-8 p-5 bg-white rounded-xl border border-gray-100">
            <div className="flex items-center gap-4">
              {report.agent.profile_pic_url
                ? <img src={report.agent.profile_pic_url} alt="" className="w-14 h-14 rounded-full object-cover" />
                : <div className="w-14 h-14 rounded-full bg-gray-100" />}
              <div>
                <p className="text-sm font-bold text-gray-900">{report.agent.name}</p>
                <p className="text-xs text-gray-500">Collings Real Estate</p>
                <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-gray-600">
                  {report.agent.phone_number && (
                    <a href={`tel:${report.agent.phone_number}`} className="inline-flex items-center gap-1 hover:text-gray-900">
                      <Phone className="w-3 h-3" /> {report.agent.phone_number}
                    </a>
                  )}
                  {report.agent.email && (
                    <a href={`mailto:${report.agent.email}`} className="inline-flex items-center gap-1 hover:text-gray-900">
                      <Mail className="w-3 h-3" /> {report.agent.email}
                    </a>
                  )}
                </div>
              </div>
            </div>
            {report.agent_blurb && (
              <p className="text-sm text-gray-700 mt-3 whitespace-pre-line">{report.agent_blurb}</p>
            )}
          </section>
        )}

        {show("property_details") && (
          <section className="mb-8">
            {heading("Property details")}
            <p className="text-sm text-gray-700">
              {[
                report.property_type,
                report.bedrooms != null && `${report.bedrooms} bed`,
                report.bathrooms != null && `${report.bathrooms} bath`,
                report.car_spaces != null && `${report.car_spaces} car`,
                report.land_size && `${Math.round(report.land_size)}m²`,
              ].filter(Boolean).join(" · ") || "No details recorded."}
            </p>
          </section>
        )}

        {show("estimate") && (saleRange || rentRange || report.appraisal_comment) && (
          <section className="mb-8">
            {heading("Price estimate")}
            <div className="grid sm:grid-cols-2 gap-3">
              {saleRange && (
                <div className="p-5 rounded-xl border bg-white border-gray-100">
                  <p className="text-xs text-gray-600">Estimated sale range</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{saleRange}</p>
                </div>
              )}
              {rentRange && (
                <div className="p-5 rounded-xl border bg-white border-gray-100">
                  <p className="text-xs text-gray-600">Estimated weekly rent</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{rentRange}</p>
                </div>
              )}
            </div>
            {report.appraisal_comment && (
              <p className="mt-3 p-4 bg-white border-l-4 rounded-r-lg text-sm text-gray-700 whitespace-pre-line"
                style={{ borderColor: theme }}>
                {report.appraisal_comment}
              </p>
            )}
          </section>
        )}

        {show("market") && tiles.length > 0 && (
          <section className="mb-8">
            {heading(`${report.suburb} demographics & median prices`)}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {tiles.map(([key, label, value]) => <Tile key={key} label={label} value={value} />)}
            </div>
          </section>
        )}

        {groups.map(([group, label]) => {
          const rows = byGroup(group);
          if (!show(group) || rows.length === 0) return null;
          return (
            <section key={group} className="mb-8">
              {heading(label)}
              <div className="space-y-2">
                {rows.map((c) => <ComparableRow key={c.id} c={c} rental={rental} />)}
              </div>
            </section>
          );
        })}

        {show("search_criteria") && (
          <section className="mb-8">
            {heading("Search criteria")}
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              {criteriaRows.map(([label, value]) => (
                <React.Fragment key={label}>
                  <dt className="text-gray-500">{label}</dt>
                  <dd className="text-gray-900">{value}</dd>
                </React.Fragment>
              ))}
            </dl>
          </section>
        )}

        <p className="text-[11px] text-gray-500 leading-relaxed border-t border-gray-200 pt-4">
          This report has been prepared by Collings Real Estate as a guide only.
          It is based on comparable sales and market information available at the date of preparation
          and is not a sworn valuation. Estimates are not a representation that the property will
          sell or lease within the stated range. You should obtain independent advice before making
          any financial decision.
        </p>
      </main>
    </div>
  );
};

export default CmaPublicReportPage;
